/**
 * PREPARACIÓN DEL PAQUETE OFFLINE (FASE 5.1)
 *
 * Orquesta lo que `offline/db.ts` no hace por sí solo: pedir el paquete al
 * servidor, persistirlo (atómico, ver `db.ts`), y descargar los PDF que
 * falten — cada uno por su cuenta, sin que el fallo de uno invalide el
 * resto (encargo B13). Mismo patrón de blob autenticado que ya usa
 * `componentes/documentos-aplicables.tsx` para abrir un documento online:
 * `fetch` + cabecera `authorization` a mano, nunca `api.get` (que asume
 * JSON).
 */
import { api, descargarConSesion, type PaqueteOffline } from '../api.ts';
import {
  documentosPendientesDeBlob,
  guardarBlobDocumento,
  leerMeta,
  purgarBlobsHuerfanos,
  reemplazarPaquete,
  type MetaOffline,
} from './db.ts';
import { cabeceraPerfilOffline, comprobarIdentidadSync, MENSAJE_PERFIL_DE_OTRO_USUARIO } from './identidad-sync.ts';

export interface ResultadoPreparacion {
  readonly paquete: PaqueteOffline;
  readonly documentosDescargados: number;
  readonly documentosFallidos: number;
}

/**
 * F14.2 — el OPERARIO no tiene `documentos:leer`: sus documentos (los de SUS OT y SUS líneas, los mismos que trae su
 * paquete) se descargan por su ruta propia, que repite el mismo filtro en el servidor.
 */
async function descargarDocumento(doc: { readonly id: string; readonly idOt: string; readonly idEvidenciaMnt?: string }, rol: PaqueteOffline['rol']): Promise<Blob | null> {
  //  F14.3 — los documentos del técnico (del activo o del parte) son evidencias de mantenimiento: su ruta de siempre,
  //  que repite el mismo filtro de ámbito que el paquete.
  if (doc.idEvidenciaMnt !== undefined) return descargarConSesion(`/api/mantenimiento/evidencias/${doc.idEvidenciaMnt}/archivo`);
  return descargarConSesion(rol === 'OPERARIO' ? `/api/mis-ots/${doc.idOt}/documentos/${doc.id}/archivo` : `/api/ot-documentos/${doc.id}/archivo`);
}

/**
 * Descarga y sustituye el paquete completo, y a continuación intenta
 * traer los PDF que aún falten. Un documento que ya tenía blob guardado
 * (mismo hash de una sincronización anterior) no vuelve a pedirse — lo
 * decide `reemplazarPaquete` en `db.ts`, no este módulo.
 */
export async function prepararPaqueteOffline(idPerfil: string): Promise<ResultadoPreparacion> {
  //  F14.2 — nunca se trae a este perfil el paquete de OTRO usuario (el de la sesión en línea). Sin sesión anunciada
  //  decide el servidor, que también rechaza una huella que no es la de la sesión.
  const identidad = await comprobarIdentidadSync(idPerfil);
  if (!identidad.ok && identidad.motivo === 'OTRO_USUARIO') {
    throw new Error(MENSAJE_PERFIL_DE_OTRO_USUARIO);
  }
  const paquete = await api.get<PaqueteOffline>('/api/offline/paquete', { headers: cabeceraPerfilOffline(idPerfil) });
  await reemplazarPaquete(idPerfil, paquete);

  const pendientes = await documentosPendientesDeBlob(idPerfil);
  const intentados = new Set<string>();
  let documentosDescargados = 0;
  let documentosFallidos = 0;

  for (const doc of pendientes) {
    //  Varias OT pueden compartir el mismo documento maestro (mismo
    //  hash) — un intento por hash en esta pasada basta, éxito o fallo:
    //  si falló, la próxima preparación lo volverá a intentar para
    //  todas las filas que lo comparten, no sólo la primera.
    if (intentados.has(doc.hashSha256)) continue;
    intentados.add(doc.hashSha256);

    const blob = await descargarDocumento(doc, paquete.rol);
    if (blob === null) {
      documentosFallidos += 1;
      continue;
    }
    await guardarBlobDocumento(idPerfil, doc.hashSha256, doc.mime, blob);
    documentosDescargados += 1;
  }

  await purgarBlobsHuerfanos(idPerfil);

  return { paquete, documentosDescargados, documentosFallidos };
}

export async function estadoLocal(idPerfil: string): Promise<MetaOffline | null> {
  return leerMeta(idPerfil);
}

/**
 * Mismo criterio que el backend (`rolOfflineDe`, `offline/servicio.ts`):
 * sólo ENCARGADO/OPERARIO (y, desde F14.3, TECNICO_MANTENIMIENTO) tienen capacidad offline (FASE 5.0 §A1). Se
 * reimplementa aquí, en el cliente, únicamente para decidir si se
 * MUESTRA la entrada de "trabajar sin conexión" — nunca para autorizar
 * nada: la autorización real la sigue haciendo el servidor en cada
 * petición, esto es sólo UX.
 */
export function tieneCapacidadOffline(roles: readonly { rol: string }[]): boolean {
  return roles.some((r) => r.rol === 'ENCARGADO' || r.rol === 'OPERARIO' || r.rol === 'TECNICO_MANTENIMIENTO');
}
